import { Box } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { NO_DRAG_SELECTOR } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import DialogHeader from "../../../common/components/functional/dialogs/DialogHeader";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import filterFalsyParameters from "../../../common/helpers/filterFalsyParameters";
import isEmpty from "../../../common/helpers/isEmpty";
import { getCoreUserFullName } from "../../../common/utils/users";
import useIsLoading from "../../../hooks/useIsLoading";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import {
  createCostCenter,
  fetchCustomerCostCenterById,
  updateCostCenter,
  updateCostCenterActiveStatus,
} from "../customerCostCentersSlice";
import { FormContainer } from "../customerManagement/styles";
import {
  fetchCustomerUsersByCompanyId,
  selectAllCustomerUsers,
} from "../customerUsersSlice";
import { ActionButtonsContainer, ModalContainer } from "../styles";
import {
  CreateCustomerCostCenter,
  UpdateCustomerCostCenter,
} from "../types/commands";
import AdditionalInfo from "./AdditionalInfo";
import BasicInfo from "./BasicInfo";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";

type Props = {
  customerCCId?: string;
  onClose: () => void;
};

const CustomerCostCentersForm = ({ customerCCId, onClose }: Props) => {
  const [version, setVersion] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const { customerCompanyId } = useParams();

  const customerOrgId = searchParams.get("org");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!customerCompanyId) return;
    dispatch(fetchCustomerUsersByCompanyId({ customerCompanyId }));
  }, [customerCompanyId, dispatch]);

  const customerUsers = useAppSelector(selectAllCustomerUsers);

  const isLoading = useIsLoading(["customerUsers", "customerCostCenters"]);

  const getDefaultValues = async () => {
    if (customerCCId) {
      const costCenterAction = await dispatch(
        fetchCustomerCostCenterById({ customerCostCenterId: customerCCId }),
      );

      if (isFulfilled(costCenterAction)) {
        setVersion(costCenterAction.payload.version);

        const {
          salesAccount,
          invoiceReferences,
          invoiceContactPerson,
          ...rest
        } = costCenterAction.payload;

        const invoiceContactPersonDefaultValue = customerUsers.find(
          (customerUser) => customerUser.id === invoiceContactPerson?.id,
        );
        const formattedInvoiceContactPerson = invoiceContactPersonDefaultValue
          ? {
              id: invoiceContactPersonDefaultValue?.id,
              name: getCoreUserFullName(invoiceContactPersonDefaultValue),
            }
          : null;

        const defaultValues = {
          ...rest,
          invoiceContactPerson: formattedInvoiceContactPerson,
          orderIdentifier: invoiceReferences?.orderIdentifier,
          agreementIdentifier: invoiceReferences?.agreementIdentifier,
          sellerReferenceIdentifier:
            invoiceReferences?.sellerReferenceIdentifier,
          buyerReferenceIdentifier: invoiceReferences?.buyerReferenceIdentifier,
          salesAccount: salesAccount || "",
        };

        return defaultValues;
      }
    }
  };

  const methods = useForm({
    defaultValues: async () => await getDefaultValues(),
    mode: "onChange",
  });

  const customerCostCenterName = methods.watch("name");

  const onSave = (data: FieldValues) => {
    const {
      name,
      code,
      invoiceContactPerson,
      orderIdentifier,
      agreementIdentifier,
      sellerReferenceIdentifier,
      buyerReferenceIdentifier,
      salesAccount,
    } = data;

    if (customerOrgId) {
      const costCenter: CreateCustomerCostCenter = {
        customerOrgId,
        name,
        code: code || "",
        invoiceReferences: {
          orderIdentifier: orderIdentifier || "",
          agreementIdentifier: agreementIdentifier || "",
          sellerReferenceIdentifier: sellerReferenceIdentifier || "",
          buyerReferenceIdentifier: buyerReferenceIdentifier || "",
        },
        ...(invoiceContactPerson?.id && {
          invoiceContactPerson: { id: invoiceContactPerson.id },
        }),
        ...(salesAccount?.code && {
          salesAccount: salesAccount.code,
        }),
      };

      if (!customerCCId) {
        return dispatch(createCostCenter(costCenter)).then((action) => {
          if (isFulfilled(action)) {
            onClose();
          }
        });
      }

      const updatedCostCenter: UpdateCustomerCostCenter = {
        id: customerCCId,
        version: version as number,
        name,
        ...filterFalsyParameters({ code }),
        invoiceReferences: {
          orderIdentifier: orderIdentifier || "",
          agreementIdentifier: agreementIdentifier || "",
          sellerReferenceIdentifier: sellerReferenceIdentifier || "",
          buyerReferenceIdentifier: buyerReferenceIdentifier || "",
        },
        ...(invoiceContactPerson?.id && {
          invoiceContactPerson: { id: invoiceContactPerson.id },
        }),
        ...(salesAccount?.code && {
          salesAccount: salesAccount.code,
        }),
      };

      dispatch(updateCostCenter(updatedCostCenter)).then((action) => {
        if (isFulfilled(action)) {
          return onClose();
        }
      });
    }
  };

  const isActive = methods.watch("active");
  const canChangeActiveStatus =
    !!customerCCId &&
    version !== null &&
    typeof isActive === "boolean" &&
    !isLoading;

  const handleStatusChange = () => {
    if (!canChangeActiveStatus) return;

    const params = {
      id: customerCCId,
      version,
      active: !isActive,
    };

    dispatch(updateCostCenterActiveStatus(params)).then((action) => {
      if (isFulfilled(action)) return onClose();
    });
  };

  const activeStatusLabel = isActive
    ? t("customers:disable")
    : t("common:active");

  const isValid = methods.formState.isValid;

  const saveDisabled =
    isEmpty(methods.formState.dirtyFields) ||
    !isEmpty(methods.formState.errors) ||
    !isValid ||
    methods.formState.isSubmitting ||
    isLoading;

  return (
    <ModalContainer sx={{ cursor: "auto" }}>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <DialogHeader title={customerCostCenterName} onClose={onClose} />
          <FormContainer maxWidth="931px" className={NO_DRAG_SELECTOR}>
            <OverlaySpinner isLoading={isLoading} />
            <Box>
              <BasicInfo />
              {customerCompanyId && (
                <AdditionalInfo customerCompanyId={customerCompanyId} />
              )}
            </Box>
          </FormContainer>
          <ActionButtonsContainer marginTop="1rem">
            <CustomButton
              accessRightChecks={[canExpertEditCustomer]}
              disabled={saveDisabled}
              variant="contained"
              sx={{ borderRadius: "18px", height: "36px" }}
              onClick={methods.handleSubmit(onSave)}
            >
              {t("common:save")}
            </CustomButton>
            <CustomButton
              accessRightChecks={[canExpertEditCustomer]}
              variant="outlined"
              color={!isActive ? "primary" : "error"}
              sx={{
                borderRadius: "18px",
                height: "36px",
                visibility: customerCCId ? "visible" : "hidden",
              }}
              disabled={!canChangeActiveStatus}
              onClick={handleStatusChange}
            >
              {activeStatusLabel}
            </CustomButton>
          </ActionButtonsContainer>
        </UnsavedChangesProvider>
      </FormProvider>
    </ModalContainer>
  );
};

export default CustomerCostCentersForm;
